/**
 * Which screens are finished enough to be seen.
 *
 * Every screen is built and on the branch. What decides whether one is
 * reachable is whether it has been through the correction pass against the
 * Figma frames, so this is a list that grows one entry at a time rather than
 * a single switch. A screen that is not listed has its route unregistered and
 * the links to it stop navigating, so nothing half corrected can be reached
 * by clicking or by pasting a URL.
 *
 * VITE_READY_SCREENS overrides the list, and "all" opens everything.
 */
export type Screen =
  | "dashboard"
  | "roles"
  | "matches"
  | "add-client"
  | "project"
  | "record-call"
  /** The assistant's question log. Not a Figma screen, so it has no
   *  correction pass to wait for; open because Paul's answer key needs it. */
  | "assistant-log"
  /** Upload a grid from the product. No Figma frame yet, so it is built to
   *  the design system and reached by URL rather than a nav link, but open
   *  because the backend route is live and the team needs a way in. */
  | "uploads";

/**
 * Open.
 *
 * dashboard, roles and the match list have been through the correction pass.
 * The other two are open because the corrected ones link straight into them:
 * the sidebar and Client Setup go to the match list, the tracking rows and
 * every row of the directory go to project details, and the sidebar button
 * goes to Add client. Gated, those are dead ends on finished screens, which
 * reads worse than an uncorrected screen does.
 *
 * record-call stays closed because nothing links to it. Opening it would
 * only make it reachable by typing the URL.
 */
const DEFAULT_READY: Screen[] = [
  "dashboard",
  "roles",
  "matches",
  "project",
  "add-client",
  "assistant-log",
  "uploads",
];

const override = import.meta.env.VITE_READY_SCREENS?.trim();

const ready: Set<string> =
  override === "all"
    ? new Set<string>([
        "dashboard",
        "roles",
        "matches",
        "add-client",
        "project",
        "record-call",
        "assistant-log",
        "uploads",
      ])
    : new Set<string>(
        override
          ? override.split(",").map((s) => s.trim()).filter(Boolean)
          : DEFAULT_READY,
      );

export const isReady = (screen: Screen): boolean => ready.has(screen);

/** True while anything is still gated. An unmatched URL then lands on the
 *  dashboard rather than on "Page not found", because the route for a gated
 *  screen simply is not registered. */
export const someGated: boolean = !isReady("record-call");
